// src/pages/PublicShare.tsx
// Public share page for collaboration mode - video + PDF viewer with annotations
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, Typography, Spin, Alert, Tabs, Space, Modal, Input } from 'antd';
import { VideoCameraOutlined, FileTextOutlined, UserOutlined } from '@ant-design/icons';
import { PDFViewer } from '../components/pdf-viewer/PDFViewer';
import type { NarrationSection } from '../components/pdf-viewer/NarrationPlayer';
import { usePublicPDFAnnotations } from '../hooks/usePublicPDFAnnotations';
import type { ScaledPosition, CreateAnnotationRequest } from '../types/annotation';

const { Title, Text } = Typography;

interface ShareData {
  videoUrl: string;
  videoTitle: string;
  companyName: string;
  companyLogo?: string;
  brandPrimary?: string;
  brandSecondary?: string;
  shareType: string;
  jobId: string;
  trackingEnabled: boolean;
  sections?: NarrationSection[];
}

export const PublicShare: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  // Extract viewer info from URL params
  const viewerParam = searchParams.get('viewer');
  let viewerEmail = '';
  if (viewerParam) {
    try {
      viewerEmail = atob(viewerParam);
    } catch (e) {
      console.error('Failed to decode viewer param:', e);
    }
  }

  const [shareData, setShareData] = useState<ShareData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('video');
  const [viewId, setViewId] = useState<string | null>(null);
  const [hasTracked, setHasTracked] = useState(false);

  // Viewer identification - either from URL param or name prompt
  const [viewerName, setViewerName] = useState<string>(viewerEmail || '');
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [nameInput, setNameInput] = useState('');

  // Work order loading no longer needed - sidebar modal handles its own loading

  // Video tracking refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const viewIdRef = useRef<string | null>(null); // Ref to access viewId in callbacks
  const totalWatchTimeRef = useRef<number>(0);
  const lastUpdateTimeRef = useRef<number>(0);
  const isPlayingRef = useRef<boolean>(false);
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  // Fetch share data from API
  useEffect(() => {
    const fetchShareData = async () => {
      if (!token) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        // Fetch share info from a new API endpoint
        const response = await fetch(`${API_URL}/share/${token}/info`);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to load share');
        }

        const data = await response.json();
        console.log('[PublicShare] Share data:', data);

        const rawSections = (data.data?.sections || data.sections) as NarrationSection[] | undefined;
        setShareData({
          videoUrl: data.data?.video_url || data.video_url,
          videoTitle: data.data?.video_title || data.video_title,
          companyName: data.data?.company_name || data.company_name,
          companyLogo: data.data?.company_logo || data.company_logo,
          brandPrimary: data.data?.brand_primary || data.brand_primary,
          brandSecondary: data.data?.brand_secondary || data.brand_secondary,
          shareType: data.data?.share_type || data.share_type,
          jobId: data.data?.job_id || data.job_id,
          trackingEnabled: data.data?.tracking_enabled || data.tracking_enabled,
          sections: rawSections && rawSections.length > 0 ? rawSections : undefined,
        });
        setLoading(false);
      } catch (err: any) {
        console.error('[PublicShare] Error loading share:', err);
        setError(err.message || 'Failed to load share');
        setLoading(false);
      }
    };

    fetchShareData();
  }, [token, API_URL]);

  // Track page view when share data is loaded and tracking is enabled
  useEffect(() => {
    const trackView = async () => {
      if (!token || !shareData || !shareData.trackingEnabled || hasTracked) {
        return;
      }

      try {
        console.log('[PublicShare] Tracking page view for share:', token);
        const response = await fetch(`${API_URL}/share/${token}/track`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            viewer_email: viewerEmail || null,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          // Backend returns {status: 'success', data: {viewId: '...'}}
          const newViewId = data.data?.viewId || data.viewId || data.view_id || data.data?.view_id;
          if (newViewId) {
            setViewId(newViewId);
            viewIdRef.current = newViewId; // Also set ref for callbacks
            console.log('[PublicShare] View tracked with ID:', newViewId);
          }
        } else {
          console.error('[PublicShare] Failed to track view:', await response.text());
        }
        setHasTracked(true);
      } catch (err) {
        console.error('[PublicShare] Error tracking view:', err);
        setHasTracked(true); // Prevent retrying on error
      }
    };

    trackView();
  }, [token, shareData, hasTracked, viewerEmail, API_URL]);

  // Video tracking functions
  const updateWatchTime = useCallback(() => {
    if (isPlayingRef.current && videoRef.current && !videoRef.current.paused) {
      const now = Date.now();
      totalWatchTimeRef.current += (now - lastUpdateTimeRef.current) / 1000;
      lastUpdateTimeRef.current = now;
    }
  }, []);

  const trackVideoEvent = useCallback(async (event: string) => {
    const currentViewId = viewIdRef.current;
    if (!currentViewId || !token || !shareData?.trackingEnabled) return;

    const video = videoRef.current;
    updateWatchTime();

    try {
      await fetch(`${API_URL}/share/${token}/track-video`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          viewId: currentViewId,
          event: event,
          currentTime: video?.currentTime || 0,
          duration: video?.duration || 0,
          watchTime: totalWatchTimeRef.current,
        }),
      });
    } catch (error) {
      console.error('[PublicShare] Error tracking video event:', error);
    }
  }, [token, shareData?.trackingEnabled, API_URL, updateWatchTime]);

  // Video event handlers - using React's event system for reliability
  const handleVideoPlay = useCallback(() => {
    const currentViewId = viewIdRef.current;
    if (!currentViewId || !shareData?.trackingEnabled) {
      console.log('[PublicShare] Video play - tracking disabled or no viewId');
      return;
    }
    console.log('[PublicShare] Video play event, viewId:', currentViewId);
    isPlayingRef.current = true;
    lastUpdateTimeRef.current = Date.now();

    // Clear any existing interval
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
    }

    // Update and send tracking every 5 seconds while playing
    updateIntervalRef.current = setInterval(() => {
      updateWatchTime();
      trackVideoEvent('playing');
    }, 5000);

    trackVideoEvent('play');
  }, [shareData?.trackingEnabled, updateWatchTime, trackVideoEvent]);

  const handleVideoPause = useCallback(() => {
    if (!viewIdRef.current || !shareData?.trackingEnabled) return;
    console.log('[PublicShare] Video pause event');
    isPlayingRef.current = false;
    updateWatchTime();

    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }

    trackVideoEvent('pause');
  }, [shareData?.trackingEnabled, updateWatchTime, trackVideoEvent]);

  const handleVideoEnded = useCallback(() => {
    if (!viewIdRef.current || !shareData?.trackingEnabled) return;
    console.log('[PublicShare] Video ended event');
    isPlayingRef.current = false;
    updateWatchTime();

    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }

    trackVideoEvent('ended');
  }, [shareData?.trackingEnabled, updateWatchTime, trackVideoEvent]);

  // Set up beforeunload handler for page close
  useEffect(() => {
    if (!viewId || !shareData?.trackingEnabled) return;

    const handleBeforeUnload = () => {
      if (isPlayingRef.current) {
        updateWatchTime();
      }

      // Use sendBeacon for reliable tracking on page unload
      if (navigator.sendBeacon && videoRef.current) {
        navigator.sendBeacon(
          `${API_URL}/share/${token}/track-video`,
          new Blob([JSON.stringify({
            viewId: viewId,
            event: 'page_unload',
            currentTime: videoRef.current.currentTime || 0,
            duration: videoRef.current.duration || 0,
            watchTime: totalWatchTimeRef.current,
          })], { type: 'application/json' })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [viewId, shareData?.trackingEnabled, token, API_URL, updateWatchTime]);

  // Use public annotations hook
  const {
    annotations,
    loading: annotationsLoading,
    fetchAnnotations,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
  } = usePublicPDFAnnotations({
    shareToken: token || '',
    viewerEmail: viewerEmail || undefined,
    viewerName: viewerName || undefined,
  });

  // Fetch annotations when switching to report tab
  useEffect(() => {
    if (activeTab === 'report' && token) {
      fetchAnnotations();
    }
  }, [activeTab, token, fetchAnnotations]);

  // Handle annotation creation - prompt for name if not identified
  const pendingAnnotationRef = useRef<CreateAnnotationRequest | null>(null);

  const handleAnnotationCreate = async (data: CreateAnnotationRequest) => {
    // If no viewer identity yet, prompt for name first
    if (!viewerEmail && !viewerName) {
      pendingAnnotationRef.current = data;
      setShowNamePrompt(true);
      return;
    }

    try {
      await createAnnotation(data);
    } catch (error: any) {
      console.error('[PublicShare] Error creating annotation:', error);
    }
  };

  // Handle name prompt submission
  const handleNameSubmit = async () => {
    const name = nameInput.trim();
    if (!name) return;

    setViewerName(name);
    setShowNamePrompt(false);

    // If there was a pending annotation, create it now
    // Need a small delay so the hook picks up the new viewerName
    if (pendingAnnotationRef.current) {
      const pendingData = pendingAnnotationRef.current;
      pendingAnnotationRef.current = null;
      // createAnnotation will use the updated viewerName on next render,
      // but we need it now - so call the API directly with the name
      try {
        const API = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        const response = await fetch(`${API}/share/${token}/annotations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: pendingData.page,
            type: pendingData.type,
            position: pendingData.position,
            comment: pendingData.comment || '',
            tags: pendingData.tags || [],
            color: pendingData.color || '#FFEB3B',
            author_name: name,
            author_email: '',
            viewer_email: '',
          }),
        });
        if (response.ok) {
          fetchAnnotations(); // Refresh the list
        } else {
          const errorData = await response.json();
          console.error('[PublicShare] Error creating annotation:', errorData.error);
        }
      } catch (error: any) {
        console.error('[PublicShare] Error creating annotation:', error);
      }
    }
  };

  // Handle annotation update
  const handleAnnotationUpdate = async (
    id: string,
    data: { comment?: string; tags?: string[]; color?: string }
  ) => {
    try {
      await updateAnnotation(id, data);
    } catch (error: any) {
      console.error('[PublicShare] Error updating annotation:', error);
    }
  };

  // Handle annotation deletion
  const handleAnnotationDelete = async (id: string) => {
    try {
      await deleteAnnotation(id);
    } catch (error: any) {
      console.error('[PublicShare] Error deleting annotation:', error);
    }
  };

  // Handle work order generation - returns Blob for modal download
  const handleGenerateWorkOrder = async (annotationIds: string[]): Promise<Blob | null> => {
    if (!token || annotationIds.length === 0) return null;

    try {
      const response = await fetch(`${API_URL}/share/${token}/generate-work-order`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ annotation_ids: annotationIds }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate work order');
      }

      return await response.blob();
    } catch (error: any) {
      console.error('[PublicShare] Error generating work order:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f0f2f5',
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f0f2f5',
        padding: '20px',
      }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (!shareData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f0f2f5',
      }}>
        <Alert
          message="Share Not Found"
          description="This share link may have expired or been revoked."
          type="warning"
          showIcon
        />
      </div>
    );
  }

  const pdfUrl = `${API_URL}/share/${token}/pdf`;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      {/* Header */}
      <div style={{
        background: shareData.brandPrimary
          ? `linear-gradient(135deg, ${shareData.brandPrimary} 0%, ${shareData.brandSecondary || shareData.brandPrimary} 100%)`
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '16px 24px',
        color: 'white',
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
          {shareData.companyLogo && (
            <img
              src={shareData.companyLogo}
              alt={shareData.companyName}
              style={{ height: '40px', borderRadius: '4px', backgroundColor: 'white', padding: '4px' }}
            />
          )}
          <Title level={4} style={{ margin: 0, color: 'white' }}>
            {shareData.companyName || 'SpectReviews'}
          </Title>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
        <Card>
          <div style={{ marginBottom: '16px' }}>
            <Title level={3} style={{ marginBottom: '4px' }}>{shareData.videoTitle}</Title>
            <Text type="secondary">Shared by {shareData.companyName}</Text>
            {(viewerEmail || viewerName) && (
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary">Viewing as: {viewerEmail || viewerName}</Text>
              </div>
            )}
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'video',
                label: (
                  <Space>
                    <VideoCameraOutlined />
                    Video Review
                  </Space>
                ),
                children: (
                  <div style={{
                    backgroundColor: '#000',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    aspectRatio: '16/9',
                    maxHeight: '70vh',
                  }}>
                    <video
                      ref={videoRef}
                      controls
                      style={{ width: '100%', height: '100%' }}
                      src={shareData.videoUrl}
                      onPlay={handleVideoPlay}
                      onPause={handleVideoPause}
                      onEnded={handleVideoEnded}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ),
              },
              {
                key: 'report',
                label: (
                  <Space>
                    <FileTextOutlined />
                    View & Comment on Report
                  </Space>
                ),
                children: (
                  <div style={{ height: 'calc(80vh - 200px)', minHeight: '600px' }}>
                    {annotationsLoading ? (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                      }}>
                        <Spin size="large" />
                      </div>
                    ) : (
                      <PDFViewer
                        pdfUrl={pdfUrl}
                        documentId={shareData.jobId}
                        annotations={annotations}
                        onAnnotationCreate={handleAnnotationCreate}
                        onAnnotationUpdate={handleAnnotationUpdate}
                        onAnnotationDelete={handleAnnotationDelete}
                        hideExport={true}
                        exportPdfUrl={`${API_URL}/share/${token}/export-pdf`}
                        enableWorkOrder={true}
                        onGenerateWorkOrder={handleGenerateWorkOrder}
                        sections={shareData.sections}
                      />
                    )}
                  </div>
                ),
              },
            ]}
          />
        </Card>
      </div>

      {/* Footer */}
      <div style={{
        textAlign: 'center',
        padding: '16px',
        color: '#999',
        fontSize: '12px',
      }}>
        Powered by <a href="https://spectreviews.com" target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>SpectReviews</a>
      </div>

      {/* Name prompt modal for anonymous commenters */}
      <Modal
        title="Enter Your Name"
        open={showNamePrompt}
        onOk={handleNameSubmit}
        onCancel={() => { setShowNamePrompt(false); pendingAnnotationRef.current = null; }}
        okText="Continue"
        okButtonProps={{ disabled: !nameInput.trim() }}
      >
        <p>Please enter your name to leave a comment:</p>
        <Input
          prefix={<UserOutlined />}
          placeholder="Your name"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          onPressEnter={handleNameSubmit}
          autoFocus
        />
      </Modal>
    </div>
  );
};
